// Dialog — Radix Dialog wrapper.
// modal·title·description·footer props. 접근성(role=dialog, aria-modal)은 Radix 자동.

import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Close,
} from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { type ReactNode } from 'react';

export interface DialogProps {
  /** 외부에서 open 상태를 제어할 때 사용 */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Trigger가 없으면 open prop으로만 제어 */
  trigger?: ReactNode;
  title: string;
  description?: string;
  /** 본문 */
  children: ReactNode;
  /** 하단 액션 영역 (버튼 등) */
  footer?: ReactNode;
  /** 최대 너비 (css 값, 기본 500px) */
  maxWidth?: string;
}

/**
 * Locker UI — Dialog
 * @example
 * <Dialog title="그룹 삭제" description="되돌릴 수 없습니다." footer={<Button>확인</Button>}>
 *   <p>내용</p>
 * </Dialog>
 */
export function Dialog({
  open,
  onOpenChange,
  trigger,
  title,
  description,
  children,
  footer,
  maxWidth = '500px',
}: DialogProps) {
  return (
    <Root open={open} onOpenChange={onOpenChange}>
      {trigger && <Trigger asChild>{trigger}</Trigger>}
      <Portal>
        <Overlay className="ui-dialog-overlay" />
        <Content
          className="ui-dialog"
          style={{ maxWidth }}
          aria-describedby={description ? 'ui-dialog-desc' : undefined}
        >
          {/* 헤더 */}
          <div className="ui-dialog__header">
            <Title className="ui-dialog__title">{title}</Title>
            <Close className="ui-dialog__close" aria-label="닫기">
              <X size={16} />
            </Close>
          </div>

          {/* 설명 (선택) */}
          {description && (
            <Description id="ui-dialog-desc" className="ui-dialog__description">
              {description}
            </Description>
          )}

          {/* 본문 */}
          <div className="ui-dialog__body">{children}</div>

          {/* 푸터 (선택) */}
          {footer && <div className="ui-dialog__footer">{footer}</div>}
        </Content>
      </Portal>
    </Root>
  );
}
